/*
CreateElection Component
*/

'use client'

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  InputLabel,
  Select,
  Slide,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Vote9Logo from "@/components/Common/Vote9Logo";

import { TransitionProps } from "@mui/material/transitions";

import React, { useRef, useState } from "react";
import copy from "clipboard-copy";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import validateEmail from "@/lib/validateemail";
import RosterEditorTable from "@/components/Common/RosterEditorTable";
import {
  getFirstNormalRosterValidationError,
  hasInvalidEmail,
  hasMissingElectorEmail,
  hasRoleError,
} from "@/lib/rosterValidation";

import ReCAPTCHA from "react-google-recaptcha";

import { theme } from "@/styles/theme";
import useSWRMutation from "swr/mutation";
import { sendRequest } from "@/utils/utils";
import { default_constraints } from "@/lib/settings";
import { IElectionSettings } from "@/interfaces/ShowNormalElectionProps";
import { electionPurposeType, electionType, normalElectionType } from "@/lib/constants";
import CreateSettings from "./CreateSettings";

import { Importer, ImporterField } from 'react-csv-importer';
// include the widget CSS file whichever way your bundler supports it
import 'react-csv-importer/dist/index.css';

interface CandidateRow {
  rowId: string;
  name: string;
  email: string;
  electee: boolean;
  elector: boolean;
}

export default function CreateElection(): JSX.Element {
  const [electionPurpose, setElectionPurpose] = useState<number>(electionPurposeType.invalid);
  const electionDesc = useRef<HTMLInputElement | null>(null);
  const [electionDescValue, setElectionDescValue] = useState<string>("");
  const electionEmail = useRef<HTMLInputElement | null>(null);
  const [electionEmailValue, setElectionEmailValue] = useState<string>("");
  const [eType, setEType] = useState<number>(0);
  const [autocomplete, setAutoComplete] = useState<number>(2);
  const [emailValid, setEmailValid] = useState(true);
  const [electionVotes, setElectionVotes] = useState<number>(1);
  const [duplicateEmail, setDuplicateEmail] = useState<boolean>(false);
  const [orderOptions, setOrderOptions] = useState<number>(0);

  const [electionSettings, setElectonSettings] = useState<IElectionSettings>({ type: 0, constraints: default_constraints })

  const [open, setOpen] = useState(false);
  const [electionCode, setElectionCode] = useState<string>("");
  const [manageSecret, setManageSecret] = useState<string>("");
  const [customSecret, setCustomSecret] = useState<string>("");
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState<string>("");

  const [candidateRows, setCandidateRows] = useState<CandidateRow[]>([]);
  const [candidateCounter, setCandidateCounter] = useState<number>(1);

  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const router = useRouter()
  const { t } = useTranslation("election");
  const { trigger } = useSWRMutation('/api/election/create', sendRequest, /* options */)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const [activeTab, setActiveTab] = useState<number>(0);
  const candidateCount = candidateRows.filter((row) => row.name.trim() !== '').length;
  const detailsComplete = electionPurpose !== electionPurposeType.invalid && eType !== 0 && electionVotes > 0;
  const candidatesComplete = candidateCount > 0;

  const renderStepTabLabel = (step: number, label: string, complete: boolean): JSX.Element => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "1px solid",
          borderColor: complete ? "success.main" : "text.disabled",
          color: complete ? "success.main" : "text.secondary",
          fontSize: "0.75rem",
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {step}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 0.1 }}>
        {label}
      </Typography>
      {complete && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "success.main" }} />}
    </Box>
  );
  
  // Construct the adhoc election URL dynamically based on current window origin
  const getAdhocElectionUrl = (code: string): string => {
    if (!code) return "";
    if (typeof window !== "undefined") {
      return `${window.location.protocol}//${window.location.host}/election/${code}`;
    }
    return `/election/${code}`;
  };
  
  const adhocElectionUrl = getAdhocElectionUrl(electionCode);

  const showCreateError = (message: string) => {
    setErrorDialogMessage(message);
    setErrorDialogOpen(true);
  };

  const addCandidateRow = () => {
    const newRow: CandidateRow = {
      rowId: `new-${candidateCounter}`,
      name: '',
      email: '',
      electee: eType === electionType.normal,
      elector: eType === electionType.normal
    };
    setCandidateRows((prev) => [...prev, newRow]);
    setCandidateCounter((prev) => prev + 1);
  };

  const updateCandidateRow = (rowId: string, updates: Partial<CandidateRow>) => {
    setCandidateRows((prev) => prev.map((row) => (row.rowId === rowId ? { ...row, ...updates } : row)));
  };

  const removeCandidateRow = (rowId: string) => {
    setCandidateRows((prev) => prev.filter((row) => row.rowId !== rowId));
  };

  const candidateRowsToElectorate = (): string => {
    return candidateRows
      .filter(row => row.name.trim() !== '')
      .map(row => {
        if (eType === electionType.adhoc) {
          return row.name;
        } else {
          // Server format: name, email, electee_flag, elector_flag
          // Flags must be 'y' or 'n'; checkFlag() only treats 'y'/'Y'/'1' as true
          return [
            row.name,
            row.email?.trim() ?? '',
            row.electee ? 'y' : 'n',
            row.elector ? 'y' : 'n',
          ].join(', ');
        }
      })
      .join('\n');
  };

  const rowHasRoleError = (row: CandidateRow) =>
    eType === electionType.normal && hasRoleError(row);

  const rowHasEmailError = (row: CandidateRow) =>
    hasInvalidEmail(row);

  const getValidationErrors = (): string[] => {
    const errors: string[] = [];
    if (electionPurpose === electionPurposeType.invalid) errors.push(t("error_purpose_required"));
    if ((electionDescValue ?? "").trim() === "") errors.push(t("error_description_required"));
    if (eType === 0) errors.push(t("error_select_type"));
    if (!emailValid) errors.push(t("error_valid_email"));
    if (eType === electionType.normal && electionSettings.type === normalElectionType.invalid) errors.push(t("error_select_ballot"));
    const namedRows = candidateRows.filter((row) => row.name.trim() !== '');
    if (namedRows.length === 0) {
      errors.push(t("error_add_candidate"));
    } else if (eType === electionType.normal) {
      const firstError = getFirstNormalRosterValidationError(namedRows, t);
      if (firstError) {
        errors.push(firstError);
      }
    }
    return errors;
  };

  const handleCreateElection = async (_event: React.MouseEvent<HTMLButtonElement>) => {
    _event.preventDefault();
    const errors = getValidationErrors();
    if (errors.length > 0) {
      showCreateError(errors[0]);
      return;
    }
    const electorate = candidateRowsToElectorate();
    const listType = eType === electionType.adhoc ? 0 : 1;
    const orderType = eType === electionType.normal ? 2 : (orderOptions ? 1 : 0);

    const captcha = await reCaptchaRef.current?.executeAsync();
      reCaptchaRef.current?.reset();


      trigger({
        name: electionPurpose,
        description: electionDescValue ?? "",
        electorate: electorate,
        votes: electionVotes,
        election_type: eType,
        list_type: listType,
        order_type: orderType,
        autocomplete_limit: autocomplete,
        email: electionEmailValue ?? "",
        duplicate_email: duplicateEmail,
        settings: electionSettings,
        ...(customSecret.trim() ? { secret: customSecret.trim() } : {}),
        captcha,
      }, {
        onSuccess: (data, _key, _config) => {
          if (!data.error) {
            setElectionCode(data.code);
            setManageSecret(data.secret);
            setOpen(true);
          } else {
            showCreateError(t("error_prefix", { msg: data.msg }))
          }
        },
        onError: (err, _key, _config) => {
          console.error("Error,", err);
          showCreateError(t("error_creating_election"));
        }
      });


  };

  const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
  });

  const handleClose = () => {
    if (eType == electionType.normal) {
      router.push(`/manage/${electionCode}`);
    } else {
      router.push(`/election/${electionCode}`);
    }
    setOpen(false);
  };

  const handleEType = (val: number) => {
    setEType(val);
    if (val === electionType.normal) {
      setOrderOptions(0);
      setCandidateRows((prev) =>
        prev.map((row) => ({
          ...row,
          electee: row.electee || row.elector ? row.electee : true,
          elector: row.electee || row.elector ? row.elector : true,
        }))
      );
    }
    if (val === electionType.adhoc) {
      setDuplicateEmail(false);
    }
  };

  return (
    <Box component="main" sx={{ width: "100%" }}>
        <ReCAPTCHA
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
          size="invisible"
          ref={reCaptchaRef}
        />
        <Box sx={{ mt: 0, display: "flex", flexDirection: "column", alignItems: "stretch", width: "100%" }}>
          <Dialog
            open={errorDialogOpen}
            onClose={() => setErrorDialogOpen(false)}
            fullScreen={isMobile}
            fullWidth
            maxWidth="sm"
            aria-labelledby="create-error-dialog-title"
            aria-describedby="create-error-dialog-description"
          >
            <DialogTitle id="create-error-dialog-title">{t("please_check_form")}</DialogTitle>
            <DialogContent>
              <DialogContentText id="create-error-dialog-description">
                {errorDialogMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setErrorDialogOpen(false)} color="primary" autoFocus>
                {t("common:ok")}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            fullScreen={isMobile}
            fullWidth
            maxWidth="sm"
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
          >
            <DialogTitle
              variant="h5"
              align="center"
              id="alert-dialog-slide-title"
            >
              {t("election_created")}
              <br />
              <b>{`${electionCode}`}</b>
            </DialogTitle>
            <DialogContent id="alert-dialog-slide-description">
              {eType == 1 ? (
                <DialogContentText component="div" variant="h6" align="center">
                  <hr />
                  {t("adhoc_url")}
                  <br />
                  {adhocElectionUrl}
                  <br />
                  {t("copy_id_url")}
                  < Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => copy(adhocElectionUrl)
                    }
                  >
                    {t("copy_url")}
                  </ Button>
                  <hr />
                </DialogContentText >
              ) : (
                <DialogContentText component="div">
                  <hr />
                  {t("normal_emails_sent")}
                  <hr />
                </DialogContentText>
              )}
              <DialogContentText component="div" variant="h6" align="center">
                {t("secret_key")} <b>{manageSecret}</b>
                <br />
                {t("note_secret_key")}
              </DialogContentText>
            </DialogContent >
            <DialogActions>
              <Button onClick={handleClose} color="primary">
                {t("common:ok")}
              </Button>
            </DialogActions>
          </Dialog >
          {/* <Avatar>
            <Check />
          </Avatar> */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Vote9Logo width={150} height={75} />
            <Typography component="div" variant="h5">
              {t("create_election")}
            </Typography>
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 1.5 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant={isMobile ? "scrollable" : "standard"}
              centered={!isMobile}
              allowScrollButtonsMobile
              sx={{
                minHeight: 56,
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderTopLeftRadius: 3,
                  borderTopRightRadius: 3,
                },
                '& .MuiTab-root': {
                  textTransform: "none",
                  minHeight: 56,
                  minWidth: { xs: 130, md: 210 },
                  px: { xs: 1, md: 2 },
                },
                '& .MuiTab-root.Mui-selected': {
                  color: "text.primary",
                },
              }}
            >
              <Tab label={renderStepTabLabel(1, t("details", { defaultValue: "Details" }), detailsComplete)} />
              <Tab label={renderStepTabLabel(2, t("candidates", { defaultValue: "Candidates" }), candidatesComplete)} />
              <Tab label={renderStepTabLabel(3, t("review_create", { defaultValue: "Review & Create" }), detailsComplete && candidatesComplete)} />
            </Tabs>
          </Box>

          <Grid2
            container
            spacing={2}
            sx={{
              width: "100%",
              mt: 1.5,
              pb: { xs: "calc(env(safe-area-inset-bottom) + 20px)", sm: 3, md: 2.5 },
            }}
          >
            {activeTab === 0 && (
              <Grid2 size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "flex-start",
                    pb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", flex: 1, minWidth: 300 }}>
                    <Tooltip title={<div>{t("purpose_of_election")}</div>}>
                      <FormControl variant="outlined" sx={{ minWidth: 140 }}>
                        <InputLabel id="election-purpose-label">
                          {t("election_purpose")}
                        </InputLabel>
                        <Select
                          labelId="election-purpose-label"
                          label={t("election_purpose")}
                          native
                          error={electionPurpose == electionPurposeType.invalid}
                          value={electionPurpose}
                          onChange={(e) => {
                            const selectedPurpose = Number(e.target.value);
                            setElectionPurpose(selectedPurpose);
                            setElectionVotes(
                              selectedPurpose === electionPurposeType.lsa || selectedPurpose === electionPurposeType.nsa
                                ? 9
                                : 1
                            );
                          }}
                          inputProps={{
                            name: "electionPurpose",
                            id: "election-purpose",
                          }}
                        >
                          <option value={electionPurposeType.invalid}>{t("purpose_select")}</option>
                          <option value={electionPurposeType.lsa}>{t("purpose_lsa")}</option>
                          <option value={electionPurposeType.rbc}>{t("purpose_rbc")}</option>
                          <option value={electionPurposeType.nsa}>{t("purpose_nsa")}</option>
                          <option value={electionPurposeType.unit}>{t("purpose_unit")}</option>
                          <option value={electionPurposeType.tie}>{t("purpose_tie")}</option>
                          <option value={electionPurposeType.office}>{t("purpose_office")}</option>
                          <option value={electionPurposeType.byelection}>{t("purpose_by_election")}</option>
                          <option value={electionPurposeType.immediate}>{t("purpose_immediate")}</option>
                        </Select>
                      </FormControl>
                    </Tooltip>

                    <Tooltip
                      title={
                        <div>
                          <b>{t("type_adhoc")}</b> - {t("type_tooltip_adhoc")}
                          <br />
                          <b>{t("type_normal")}</b> - {t("type_tooltip_normal")}
                        </div>
                      }
                    >
                      <FormControl variant="outlined" sx={{ minWidth: 120 }}>
                        <InputLabel id="type-label">{t("type")}</InputLabel>
                        <Select
                          labelId="type-label"
                          label={t("type")}
                          native
                          error={eType == 0}
                          value={eType}
                          onChange={(e) => {
                            handleEType(Number(e.target.value));
                          }}
                        >
                          <option value={0}>{t("purpose_select")}</option>
                          <option value={1}>{t("type_adhoc")}</option>
                          <option value={2}>{t("type_normal")}</option>
                        </Select>
                      </FormControl>
                    </Tooltip>

                    <Tooltip title={<div>{t("number_of_votes")}</div>}>
                      <FormControl variant="outlined" sx={{ minWidth: 80 }}>
                        <InputLabel id="votes-per-ballot-label">
                          {t("votes")}
                        </InputLabel>
                        <Select
                          labelId="votes-per-ballot-label"
                          label={t("votes")}
                          native
                          value={electionVotes}
                          onChange={(e) => {
                            setElectionVotes(Number(e.target.value));
                          }}
                          inputProps={{
                            name: "votes",
                            id: "votes-per-ballot",
                          }}
                        >
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                          <option value={3}>3</option>
                          <option value={4}>4</option>
                          <option value={5}>5</option>
                          <option value={6}>6</option>
                          <option value={7}>7</option>
                          <option value={8}>8</option>
                          <option value={9}>9</option>
                        </Select>
                      </FormControl>
                    </Tooltip>

                    <Tooltip
                      title={
                        <div>
                          {t("acl_tooltip")}
                          <br />
                          {t("acl_tooltip_detail")}
                        </div>
                      }
                    >
                      <FormControl
                        variant="outlined"
                        disabled={eType !== electionType.normal}
                        sx={{ minWidth: 80 }}
                      >
                        <InputLabel id="autocomplete-limit-label">
                          {t("acl")}
                        </InputLabel>
                        <Select
                          labelId="autocomplete-limit-label"
                          label={t("acl")}
                          native
                          value={autocomplete}
                          onChange={(e) => {
                            setAutoComplete(Number(e.target.value));
                          }}
                          inputProps={{
                            name: "acl",
                            id: "autocomplete-limit",
                          }}
                        >
                          <option value={0}>1</option>
                          <option value={1}>2</option>
                          <option value={2}>3</option>
                        </Select>
                      </FormControl>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {eType === electionType.normal && (
                      <Tooltip
                        title={
                          <div>
                            {t("allow_duplicate_tooltip")}
                          </div>
                        }
                      >
                        <FormControlLabel
                          sx={{ m: 0, '& .MuiFormControlLabel-label': { lineHeight: 1.2, fontSize: "0.9rem" } }}
                          control={
                            <Checkbox
                              checked={duplicateEmail}
                              onChange={(e) => {
                                setDuplicateEmail(e.target.checked as boolean);
                              }}
                              inputProps={{
                                "aria-label": t("aria_allow_duplicate_emails"),
                              }}
                            />
                          }
                          label={t("allow_duplicate_email")}
                        />
                      </Tooltip>
                    )}
                    {eType === electionType.adhoc && (
                      <Tooltip
                        title={
                          <div>
                            {t("randomise_tooltip")}
                          </div>
                        }
                      >
                        <FormControlLabel
                          sx={{ m: 0, '& .MuiFormControlLabel-label': { lineHeight: 1.2, fontSize: "0.9rem" } }}
                          control={
                            <Checkbox
                              checked={orderOptions == 1}
                              onChange={(e) => {
                                setOrderOptions(e.target.checked ? 1 : 0);
                              }}
                              inputProps={{ "aria-label": t("aria_randomise_options") }}
                            />
                          }
                          label={t("randomise_order")}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "2fr 1fr 1fr" },
                    gap: 1.5,
                    mt: 2,
                    mb: 1.5,
                  }}
                >
                  <TextField
                    variant="outlined"
                    margin="none"
                    fullWidth
                    id="electionDescription"
                    label={t("description")}
                    required
                    inputRef={electionDesc}
                    value={electionDescValue}
                    onChange={(e) => setElectionDescValue(e.target.value)}
                  />
                  <TextField
                    variant="outlined"
                    margin="none"
                    fullWidth
                    id="electionEmail"
                    label={t("notification_email")}
                    inputRef={electionEmail}
                    error={!emailValid}
                    value={electionEmailValue}
                    onChange={(e) => {
                      setElectionEmailValue(e.target.value);
                      setEmailValid(e.target.value == "" || validateEmail(e.target.value));
                    }}
                  />
                  <Tooltip title={<div>{t("custom_secret_tooltip")}</div>}>
                    <TextField
                      variant="outlined"
                      margin="none"
                      fullWidth
                      id="customSecret"
                      label={t("custom_secret_optional")}
                      type="text"
                      value={customSecret}
                      inputProps={{ maxLength: 64 }}
                      onChange={(e) => setCustomSecret(e.target.value)}
                      helperText={t("custom_secret_helper")}
                    />
                  </Tooltip>
                </Box>

                {eType === electionType.normal && (
                  <Box sx={{ mt: 1.5 }}>
                    <Collapse in={!isMobile || eType === electionType.normal}>
                      <CreateSettings electionSettings={electionSettings} setElectonSettings={setElectonSettings} />
                    </Collapse>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 2,
                    mb: { xs: 1, sm: 2 },
                  }}
                >
                  <Button variant="contained" onClick={() => setActiveTab(1)}>
                    {t("next", { defaultValue: "Next: Candidates" })}
                  </Button>
                </Box>
              </Grid2>
            )}

            {activeTab === 1 && (
              <Grid2 size={{ xs: 12 }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  mt: 1,
                  mb: 0.75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle1" sx={{ m: 0 }}>
                  {t("candidates")}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size={isMobile ? "medium" : "small"}
                  onClick={addCandidateRow}
                  disabled={eType === 0}
                  fullWidth={isMobile}
                >
                  {t("add_row")}
                </Button>
              </Box>
              {isMobile ? (
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, px: 0.75, py: 0 }}>
                  {candidateRows.map((row, index) => (
                    <Box
                      key={row.rowId}
                      sx={{
                        pt: 0.35,
                        pb: 0.3,
                        borderBottom: index === candidateRows.length - 1 ? "none" : "1px solid",
                        borderColor: "divider",
                        ...(rowHasRoleError(row) || rowHasEmailError(row) ? {
                          outline: "1.5px solid",
                          outlineColor: "error.main",
                          borderRadius: 1,
                          px: 0.5,
                        } : {}),
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <TextField
                            fullWidth
                            size="small"
                            hiddenLabel
                            placeholder={eType === electionType.adhoc ? t("name_option") : t("common:name")}
                            value={row.name}
                            onChange={(e) => updateCandidateRow(row.rowId, { name: e.target.value })}
                            inputProps={{ "aria-label": eType === electionType.adhoc ? t("name_option") : t("common:name") }}
                            sx={{ '& .MuiOutlinedInput-root': { height: 34 } }}
                          />
                        </Box>
                        {eType === electionType.normal && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.2, whiteSpace: "nowrap", flexShrink: 0 }}>
                            <FormControlLabel
                              sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem' } }}
                              control={
                                <Checkbox
                                  checked={row.electee}
                                  onChange={(e) => updateCandidateRow(row.rowId, { electee: e.target.checked })}
                                  size="small"
                                  sx={{ p: 0.2 }}
                                />
                              }
                              label={t("electee")}
                            />
                            <FormControlLabel
                              sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem' } }}
                              control={
                                <Checkbox
                                  checked={row.elector}
                                  onChange={(e) => updateCandidateRow(row.rowId, { elector: e.target.checked })}
                                  size="small"
                                  sx={{ p: 0.2 }}
                                />
                              }
                              label={t("elector")}
                            />
                          </Box>
                        )}
                        {eType === electionType.adhoc && (
                          <Box sx={{ width: 28, display: "flex", justifyContent: "center" }}>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => removeCandidateRow(row.rowId)}
                              aria-label={t("common:action")}
                              sx={{ p: 0.2 }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {eType === electionType.normal && (
                        <Box sx={{ mt: 0.35, display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <TextField
                              fullWidth
                              size="small"
                              hiddenLabel
                              placeholder={t("common:email")}
                              value={row.email}
                              onChange={(e) => updateCandidateRow(row.rowId, { email: e.target.value })}
                              error={rowHasEmailError(row)}
                              inputProps={{ "aria-label": t("common:email") }}
                              sx={{ '& .MuiOutlinedInput-root': { height: 34 } }}
                            />
                          </Box>
                          <Box sx={{ width: 28, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => removeCandidateRow(row.rowId)}
                              aria-label={t("common:delete")}
                              sx={{ p: 0.2 }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <RosterEditorTable
                  rows={candidateRows}
                  editable={true}
                  nameLabel={eType === electionType.adhoc ? t("name_option") : t("common:name")}
                  emailLabel={t("common:email")}
                  electeeLabel={t("electee")}
                  electorLabel={t("elector")}
                  actionLabel={t("common:action")}
                  removeLabel={t("common:delete")}
                  showEmailAndRoles={eType === electionType.normal}
                  compactHeaders={isMdDown && eType === electionType.normal}
                  containerSx={{ maxHeight: { xs: 400, md: 500 } }}
                  onRowChange={updateCandidateRow}
                  onRowRemove={removeCandidateRow}
                  rowHasError={(row) =>
                    eType === electionType.normal && (hasRoleError(row) || hasMissingElectorEmail(row) || hasInvalidEmail(row))
                  }
                  emailHasError={(row) => hasInvalidEmail(row)}
                />
              )}
              {candidateRows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                  {eType === 0 ? t("choose_type_to_enable") : t("no_candidates")}
                </Typography>
              )}
              {eType === electionType.normal && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                  {t("normal_election_requirements")}
                </Typography>
              )}
              <Accordion sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">{t("import_from_csv")}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const isNormal = eType === electionType.normal;
                        const header = isNormal
                          ? 'name,email,electee,elector'
                          : 'name';
                        const example = isNormal
                          ? '\nAlice Smith,alice@example.com,true,true\nBob Jones,bob@example.com,true,false'
                          : '\nOption A\nOption B';
                        const content = header + example;
                        const el = document.createElement('a');
                        el.href = URL.createObjectURL(new Blob([content], { type: 'text/csv' }));
                        el.download = isNormal ? t("csv_template_normal") : t("csv_template_adhoc");
                        document.body.appendChild(el);
                        el.click();
                        document.body.removeChild(el);
                      }}
                    >
                      {t("download_csv_template")}
                    </Button>
                  </Box>
                  <Importer
                    dataHandler={async (rows) => {
                      for (const row of rows as Array<Record<string, string>>) {
                        const newRow: CandidateRow = {
                          rowId: `import-${candidateCounter}-${Math.random()}`,
                          name: row.name || '',
                          email: row.email || '',
                          electee: row.electee?.toLowerCase() === 'true' || row.electee === '1' || eType === electionType.normal,
                          elector: row.elector?.toLowerCase() === 'true' || row.elector === '1' || eType === electionType.normal
                        };
                        setCandidateRows((prev) => [...prev, newRow]);
                        setCandidateCounter((prev) => prev + 1);
                      }
                    }}
                    defaultNoHeader={false}
                    restartable={true}
                  >
                    <ImporterField name="name" label={t("common:name")} />
                    <ImporterField name="email" label={t("common:email")} optional />
                    <ImporterField name="electee" label={t("electee")} optional />
                    <ImporterField name="elector" label={t("elector")} optional />
                  </Importer>
                </AccordionDetails>
              </Accordion>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                  mb: { xs: 1, sm: 2 },
                }}
              >
                <Button variant="outlined" onClick={() => setActiveTab(0)}>
                  {t("back", { defaultValue: "Back" })}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveTab(2)}
                >
                  {t("next", { defaultValue: "Next: Review" })}
                </Button>
              </Box>
              </Grid2>
            )}

            {activeTab === 2 && (
              <Grid2 size={{ xs: 12 }}>
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    {t("review_create", { defaultValue: "Review & Create" })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t("type", { defaultValue: "Type" })}: {eType === electionType.normal ? t("type_normal") : eType === electionType.adhoc ? t("type_adhoc") : t("purpose_select")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t("votes", { defaultValue: "Votes" })}: {electionVotes}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t("candidates", { defaultValue: "Candidates" })}: {candidateRows.filter((row) => row.name.trim() !== "").length}
                  </Typography>
                </Box>
                {(() => {
                  const errors = getValidationErrors();
                  return errors.length > 0 ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                        {errors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    </Alert>
                  ) : null;
                })()}

                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    mb: { xs: 1, sm: 2 },
                    borderTop: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Button variant="outlined" onClick={() => setActiveTab(1)}>
                    {t("back", { defaultValue: "Back" })}
                  </Button>
                  <Button
                    fullWidth={isMobile}
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { sm: 220 },
                      px: { sm: 4 },
                    }}
                    onClick={(e) => handleCreateElection(e)}
                  >
                    {t("create_button")}
                  </Button>
                </Box>
              </Grid2>
            )}

          </Grid2>{/* end outer grid */}
          {/* </form> */}
        </Box>
      </Box>
  );
}
